import { useCarousel } from '@lastbrand/web-ui/carousel'
import { useEffect, useState } from 'react'

/**
 * Guards against sub-pixel scroll positions leaving an edge "overflowing" by a fraction of a
 * pixel once the track has visually come to rest at a limit.
 */
const EDGE_PROGRESS_EPSILON = 0.001

export interface CarouselEdgeOverflow {
  /** The track currently has slides scrolled out past its left edge. */
  hasPrevOverflow: boolean
  /** The track currently has slides scrolled out past its right edge. */
  hasNextOverflow: boolean
}

const NO_OVERFLOW: CarouselEdgeOverflow = { hasPrevOverflow: false, hasNextOverflow: false }

/**
 * Which edges of a carousel currently have slides scrolled out past them, derived from the
 * track's real scroll **position**.
 *
 * Prefer this over `useCarousel()`'s `canScrollPrev` / `canScrollNext` for anything painted on
 * an edge — fades, masks, clips. Those two answer "is there another snap to scroll to", which
 * is a property of the *target* index, so embla flips them the instant a scroll is committed:
 * on `select` at progress 0 for an arrow press (verified against the running app), and not at
 * all until pointer-up for a drag. Either way they disagree with what is on screen for the
 * length of the movement — the edge treatment lands hundreds of milliseconds before or after
 * the slides it exists to cover (STRFRNT-15364). The flags remain the right source for
 * *affordances* like the arrow buttons, where "is there another page to request" is exactly
 * the question.
 *
 * `proximityPx` widens what counts as "at" a limit, in px of track travel. Embla's easing is
 * asymptotic — the track visually stops well before it finishes crawling the last fraction of
 * a pixel (~450ms on the CLP category bubbles) — and edge chrome held to the strict epsilon
 * outstays the motion it exists to cover. Affordances (fades, arrows) pass a few px so they
 * leave the moment the track *looks* parked; paint-gating (the focus clip) keeps the strict
 * default, since a side that un-clips early leaks that many px of slide past the section edge.
 * The px range comes from `internalEngine().limit` — the only px source: this embla version
 * reports `scrollSnapList()` positions as 0..1 progress fractions, and the container/root
 * nodes report layout width, not content width.
 *
 * Must be called inside a `Carousel` (it reads the embla context).
 */
export function useCarouselEdgeOverflow(proximityPx = 0): CarouselEdgeOverflow {
  const { emblaApi } = useCarousel()
  const [edgeOverflow, setEdgeOverflow] = useState<CarouselEdgeOverflow>(NO_OVERFLOW)

  useEffect(() => {
    if (!emblaApi) {
      setEdgeOverflow(NO_OVERFLOW)
      return undefined
    }

    const sync = () => {
      // A track whose slides all fit has a single snap and a progress embla leaves at 0 —
      // neither edge overflows, so gate on the snap count before reading progress.
      const scrolls = emblaApi.scrollSnapList().length > 1
      const progress = emblaApi.scrollProgress()
      const rangePx = Math.abs(emblaApi.internalEngine().limit.length)
      const next: CarouselEdgeOverflow = {
        hasPrevOverflow: scrolls && progress > EDGE_PROGRESS_EPSILON && progress * rangePx > proximityPx,
        hasNextOverflow: scrolls && progress < 1 - EDGE_PROGRESS_EPSILON && (1 - progress) * rangePx > proximityPx,
      }

      // `scroll` fires every animation frame; only re-render on an actual edge change.
      setEdgeOverflow(current =>
        current.hasPrevOverflow === next.hasPrevOverflow && current.hasNextOverflow === next.hasNextOverflow
          ? current
          : next,
      )
    }

    sync()
    emblaApi.on('reInit', sync).on('scroll', sync).on('settle', sync)

    return () => {
      emblaApi.off('reInit', sync).off('scroll', sync).off('settle', sync)
    }
  }, [emblaApi, proximityPx])

  return edgeOverflow
}
